import { apiFetch } from '../../lib/api-client';
import type {
    ComputeDashboardCatalogItem,
    ComputeDashboardResult,
    ComputeDashboardView,
    ComputeDashboardVisual,
    ComputeDistributionView,
    DashboardSelections,
    DashboardFilterOptions,
} from './dashboardTypes';
import type { ComputeFilters, ComputeValue } from './computeTypes';

const DASHBOARD_API_ROOT = '/api/service/SPO/compute/dashboards';

async function readDashboardJson<T>(response: Response, failure: string): Promise<T> {
    if (!response.ok) {
        const body = await response.json().catch(() => null) as { error?: string; title?: string } | null;
        throw new Error(body?.error ?? body?.title ?? `${failure} (${response.status}).`);
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.toLowerCase().includes('json')) {
        throw new Error(`${failure}: the dashboards API returned an unexpected response. It may not be running.`);
    }

    return response.json() as Promise<T>;
}

export async function loadComputeDashboards(signal: AbortSignal): Promise<ComputeDashboardCatalogItem[]> {
    const response = await apiFetch(DASHBOARD_API_ROOT, { signal });
    return readDashboardJson<ComputeDashboardCatalogItem[]>(response, 'Dashboard list request failed');
}

export async function loadDashboardFilterOptions(id: string, signal: AbortSignal, filterId?: string): Promise<DashboardFilterOptions> {
    const suffix = filterId ? `/${encodeURIComponent(filterId)}` : '';
    const response = await apiFetch(`${DASHBOARD_API_ROOT}/${encodeURIComponent(id)}/filters${suffix}`, { signal });
    if (filterId) {
        const values = await readDashboardJson<string[]>(response, 'Filter options request failed');
        return { [filterId]: values };
    }
    return readDashboardJson<DashboardFilterOptions>(response, 'Filter options request failed');
}

export function hasDashboardVisuals(result?: { visuals?: readonly unknown[] } | null): boolean {
    return (result?.visuals?.length ?? 0) > 0;
}

export async function loadComputeDashboard(
    id: string,
    filters: ComputeFilters,
    signal: AbortSignal,
    selections: DashboardSelections = {},
): Promise<ComputeDashboardView> {
    const query = new URLSearchParams({ startDate: filters.startDate, endDate: filters.endDate });
    if (filters.geo) query.set('geo', filters.geo);
    if (filters.farm) query.set('farmLabel', filters.farm);
    if (filters.customerSegment) query.set('customerSegment', filters.customerSegment);
    for (const [id, value] of Object.entries(selections)) {
        if (Array.isArray(value)) {
            for (const item of value) query.append(`${id}[]`, item);
        } else if (value.trim()) query.set(id, value.trim());
    }

    const response = await apiFetch(`${DASHBOARD_API_ROOT}/${encodeURIComponent(id)}?${query}`, { signal });
    const result = await readDashboardJson<ComputeDashboardResult>(response, 'Dashboard request failed');
    return toDashboardView(result, filters.granularity);
}

// Turns a config dashboard result into the same view model the built-in report pages render.
export function toDashboardView(
    result: ComputeDashboardResult,
    granularity: ComputeFilters['granularity'] = 'day',
): ComputeDashboardView {
    const x = result.charts[0]?.x ?? '';
    const byDate = result.columns.some(column => column.name === x && column.type === 'date');
    const hasVisuals = hasDashboardVisuals(result);
    // Distribution visuals require every date/category row, never a latest-row snapshot; number x rows are never grouped.
    const rows = hasVisuals || !byDate ? result.rows : groupRows(result.rows, x, granularity);
    const labelFor = (name: string) => result.columns.find(column => column.name === name)?.label || name;
    const othersColumns = new Set(result.visuals?.flatMap(visual => visual.othersColumn ? [visual.othersColumn] : []));
    const tableRows = othersColumns.size ? rows.map(row => {
        const display = { ...row };
        for (const visual of result.visuals ?? []) {
            if (visual.othersColumn && row[visual.othersColumn] === 1) display[visual.category] = 'Others (aggregated)';
        }
        return display;
    }) : rows;
    const toSeries = (column: string) => ({
        key: column,
        label: labelFor(column),
        points: rows
            .filter(row => (typeof row[x] === 'string' || typeof row[x] === 'number') && typeof row[column] === 'number')
            .map(row => ({ timestamp: String(row[x]), value: row[column] as number })),
    });
    const charts = hasVisuals ? [] : result.charts.map(chart => ({
        title: chart.title ?? null,
        type: chart.type,
        xAxis: byDate ? 'time' as const : 'category' as const,
        series: chart.series.map(toSeries),
        bars: (chart.bars ?? []).map(toSeries),
    }));
    return {
        title: result.title,
        description: result.description,
        filters: result.filters,
        freshness: {
            generatedAt: result.generatedAtUtc,
            dataThrough: result.dataThrough ?? null,
            isStale: false,
        },
        warnings: [],
        summary: result.summary.map(metric => ({
            key: `${metric.column}-${metric.aggregate}`,
            label: metric.aggregate === 'latest' ? metric.label : `${metric.label} (${metric.aggregate})`,
            value: metric.value ?? 'Unavailable',
            status: 'neutral' as const,
        })),
        visuals: result.visuals?.map(visual => toDistributionView(rows, x, visual, labelFor(visual.value), labelFor(visual.category))),
        charts,
        series: charts[0]?.series ?? [],
        table: {
            columns: result.columns.filter(column => !othersColumns.has(column.name)).map(column => ({
                key: column.name,
                label: labelFor(column.name),
                format: column.type === 'date' ? 'date' as const : column.type === 'number' ? 'number' as const : 'text' as const,
            })),
            rows: tableRows,
        },
    };
}

export function toDistributionView(
    rows: Record<string, ComputeValue>[],
    dateColumn: string,
    visual: ComputeDashboardVisual,
    valueLabel: string,
    categoryLabel: string,
): ComputeDistributionView {
    const periodOnly = visual.type === 'pie' || visual.type === 'distributionTable';
    const byCategoryDate = new Map<string, Map<string, number>>();
    const byDate = new Map<string, number>();
    const byCategory = new Map<string, number>();
    const categories = new Map<string, { label: string; isOthers?: boolean; filterValue?: string }>();
    for (const row of rows) {
        const date = row[dateColumn];
        const category = row[visual.category];
        const value = row[visual.value];
        if (typeof date !== 'string' || typeof category !== 'string'
            || typeof value !== 'number' || !Number.isFinite(value)) {
            throw new Error(`Invalid data for dashboard visual "${visual.title}".`);
        }
        const isOthers = visual.othersColumn ? row[visual.othersColumn] === 1 : false;
        const key = JSON.stringify([isOthers, category]);
        const label = isOthers ? 'Others' : category === '' ? '(Blank)' : visual.othersColumn && category === 'Others' ? 'Others (category)' : category;
        categories.set(key, {
            label,
            ...(isOthers ? { isOthers: true } : label !== category ? { filterValue: category } : {}),
        });
        if (!periodOnly) {
            const observations = byCategoryDate.get(key) ?? new Map<string, number>();
            observations.set(date, (observations.get(date) ?? 0) + value);
            byCategoryDate.set(key, observations);
            byDate.set(date, (byDate.get(date) ?? 0) + value);
        }
        byCategory.set(key, (byCategory.get(key) ?? 0) + value);
    }
    const dates = [...byDate.keys()].sort();
    const totals = dates.map(date => byDate.get(date)!);
    return {
        type: visual.type,
        title: visual.title,
        valueLabel,
        categoryLabel,
        filterId: visual.filterId,
        dates,
        series: [...byCategoryDate.keys()].sort((a, b) =>
            Number(!!categories.get(a)!.isOthers) - Number(!!categories.get(b)!.isOthers) ||
            categories.get(a)!.label.localeCompare(categories.get(b)!.label)).map(key => ({
            ...categories.get(key)!,
            points: [...byCategoryDate.get(key)!].sort(([left], [right]) => left.localeCompare(right))
                .map(([date, value]) => ({
                    x: date,
                    y: visual.type === 'percentStackedColumn'
                        ? byDate.get(date)! > 0 ? 100 * value / byDate.get(date)! : null
                        : value,
                })),
        })),
        totals,
        slices: [...byCategory].map(([key, value]) => ({ ...categories.get(key)!, value }))
            .sort((left, right) => Number(!!left.isOthers) - Number(!!right.isOthers) ||
                right.value - left.value || left.label.localeCompare(right.label)),
    };
}

// Same rule as the built-in pages: each week or month shows its latest daily row.
function groupRows(
    rows: Record<string, ComputeValue>[],
    x: string,
    granularity: ComputeFilters['granularity'],
): Record<string, ComputeValue>[] {
    if (granularity === 'day') {
        return rows;
    }

    const grouped = new Map<string, Record<string, ComputeValue>>();
    for (const row of rows) {
        const value = row[x];
        if (typeof value !== 'string') continue;
        const date = new Date(`${value}T00:00:00Z`);
        const key = granularity === 'month'
            ? `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
            : startOfWeek(date);
        const existing = grouped.get(key);
        if (!existing || value.localeCompare(existing[x] as string) > 0) {
            grouped.set(key, row);
        }
    }
    return [...grouped.values()].sort((left, right) => (left[x] as string).localeCompare(right[x] as string));
}

function startOfWeek(date: Date): string {
    const result = new Date(date);
    result.setUTCDate(result.getUTCDate() - ((result.getUTCDay() + 6) % 7));
    return result.toISOString().slice(0, 10);
}
